import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, Globe2, LogOut, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import * as AuthModule from 'modules/auth';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import { useStudioNavGroups } from 'components/global/LeftSidebarContent';
import { useLanguageStore, type Language } from 'store/language.store';
import { useThemeStore, type Theme } from 'store/theme.store';
import { M } from './theme';

const LANGUAGE_ORDER: Language[] = ['uz', 'en', 'ru'];
const LANGUAGE_LABELS: Record<Language, string> = { uz: "O'zbek", en: 'English', ru: 'Русский' };
const THEME_ORDER: Theme[] = ['light', 'dark', 'system'];
const THEME_ICON: Record<Theme, LucideIcon> = { light: Sun, dark: Moon, system: Monitor };

function nextOption<T>(options: readonly T[], value: T): T {
  return options[(options.indexOf(value) + 1) % options.length];
}

/** Destinations come from the shared, permission-gated studio nav, so it never shows an unreachable tab. */
export function MoreSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { groups } = useStudioNavGroups();
  const { open: openSettings } = useSettingsDialog();
  const { language, setLanguage } = useLanguageStore();
  const { theme, setTheme } = useThemeStore();
  const {
    state: { user, role },
    methods,
  } = AuthModule.Context.useAuthContext();

  if (!open) return null;

  const tiles = groups.flatMap((g) => g.items);
  const initials =
    user?.name
      ?.split(' ')
      .map((s) => s.charAt(0))
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U';
  const roleLabel = role
    ? role
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : '';
  const ThemeIcon = THEME_ICON[theme];

  return (
    <>
      <div
        className="absolute inset-0 z-[46] animate-in fade-in-0"
        style={{ background: 'rgba(17,24,39,.5)' }}
        onClick={onClose}
      />
      <div
        className="absolute inset-x-0 bottom-0 z-[47] max-h-[84%] animate-in slide-in-from-bottom-4 overflow-y-auto rounded-t-lg px-4 pt-2 scrollbar-thin"
        style={{
          background: M.card,
          boxShadow: '0 -14px 44px rgba(17,24,39,.24)',
          paddingBottom: 'max(1.375rem, env(safe-area-inset-bottom))',
        }}
      >
        <div className="flex justify-center py-2 pb-3.5">
          <span className="h-[5px] w-[42px] rounded-full" style={{ background: M.chevron }} />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <span className="text-[17px] font-extrabold tracking-[-0.02em]" style={{ color: M.ink }}>
            {t('all_destinations', 'All destinations')}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close', 'Close')}
            className="flex h-8 w-8 items-center justify-center rounded-md"
            style={{ background: M.muted, color: M.ink3 }}
          >
            <X size={17} />
          </button>
        </div>

        <div className="mb-3.5 grid grid-cols-4 gap-1.5">
          {tiles.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="flex flex-col items-center gap-1.5 rounded-md py-3 transition-colors active:bg-[color:var(--m-muted)]"
              style={{ ['--m-muted' as string]: M.muted }}
            >
              <span
                className="relative flex h-12 w-12 items-center justify-center rounded-lg"
                style={{
                  background: item.active ? M.accentTint : M.muted,
                  color: item.active ? M.accent : M.ink2,
                }}
              >
                <item.icon size={23} strokeWidth={1.75} />
                {item.badge != null && item.badge > 0 && (
                  <span
                    className="absolute -right-1.5 -top-1.5 inline-flex h-[19px] min-w-[19px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white"
                    style={{ background: M.redBright, border: '2px solid #fff' }}
                  >
                    {item.badge}
                  </span>
                )}
              </span>
              <span
                className="w-full truncate px-0.5 text-center text-[11px] font-semibold"
                style={{ color: item.active ? M.accent : M.ink2 }}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </div>

        <div
          className="mb-3 grid grid-cols-2 gap-2 border-t pt-3"
          style={{ borderColor: M.divider }}
        >
          <button
            type="button"
            data-more-preference="language"
            onClick={() => setLanguage(nextOption(LANGUAGE_ORDER, language))}
            aria-label={`${t('settings.display_language', 'Language')}: ${LANGUAGE_LABELS[language]}`}
            className="flex min-h-[52px] min-w-0 items-center gap-2 rounded-md border px-3 text-left transition-transform duration-150 active:scale-[0.96]"
            style={{ background: M.card, borderColor: M.border, color: M.ink }}
          >
            <Globe2 size={18} strokeWidth={1.75} style={{ color: M.ink3 }} aria-hidden />
            <span className="min-w-0">
              <span className="block text-xs font-semibold">{t('settings.display_language', 'Language')}</span>
              <span className="block truncate text-xs" style={{ color: M.ink3 }}>
                {LANGUAGE_LABELS[language]}
              </span>
            </span>
          </button>
          <button
            type="button"
            data-more-preference="theme"
            onClick={() => setTheme(nextOption(THEME_ORDER, theme))}
            aria-label={`${t('usermenu_theme', 'Appearance')}: ${t(`usermenu_theme${theme}`)}`}
            className="flex min-h-[52px] min-w-0 items-center gap-2 rounded-md border px-3 text-left transition-transform duration-150 active:scale-[0.96]"
            style={{ background: M.card, borderColor: M.border, color: M.ink }}
          >
            <ThemeIcon size={18} strokeWidth={1.75} style={{ color: M.ink3 }} aria-hidden />
            <span className="min-w-0">
              <span className="block text-xs font-semibold">{t('usermenu_theme', 'Appearance')}</span>
              <span className="block truncate text-xs" style={{ color: M.ink3 }}>
                {t(`usermenu_theme${theme}`)}
              </span>
            </span>
          </button>
        </div>

        {/* Account row — opens Profile settings; sign out sits beside the
            disclosure chevron as its own control (a button can't nest inside
            the row button, so the two are siblings in a flex row). */}
        <div
          className="flex items-center gap-1 pt-2.5"
          style={{ borderTop: `1px solid ${M.divider}` }}
        >
          <button
            type="button"
            onClick={() => {
              onClose();
              openSettings('profile');
            }}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-2.5 text-left"
          >
            <span
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold"
              style={{ background: M.primary, color: M.primaryInk }}
            >
              {initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold" style={{ color: M.ink }}>
                {user?.name || t('account', 'Account')}
              </span>
              <span className="block truncate text-xs" style={{ color: M.faint }}>
                {[user?.email, roleLabel].filter(Boolean).join(' · ')}
              </span>
            </span>
            <ChevronRight size={18} style={{ color: M.chevron }} />
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              methods.logout();
            }}
            aria-label={t('usermenu_signout')}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md"
            style={{ background: M.redTint, color: M.redBright }}
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </>
  );
}

export default MoreSheet;
